import dynStorage from '@/utils/storage-dynamic';
import axios from 'axios';
import firebase from 'firebase/app';
import * as Sentry from '@sentry/vue';

import { db } from '@/utils/firebase.js';

import i18n from '@/i18n'

// Resolve once account_customers_uuids is populated after a Firestore bind.
function waitForAccountCustomers(store, getters) {
  if (getters.account_customers_uuids?.length) {
    return Promise.resolve(getters.account_customers_uuids);
  }

  return new Promise((resolve) => {
    let settled = false;

    const finish = (value) => {
      if (settled) return;
      settled = true;
      unwatch();
      clearTimeout(timer);
      resolve(value);
    };

    const unwatch = store.watch(
      (_state, g) => g.account_customers_uuids,
      (uuids) => {
        if (uuids?.length) finish(uuids);
      }
    );

    // Safety net: never hang the create flow if the bind never emits.
    const timer = setTimeout(() => finish(getters.account_customers_uuids), 15000);
  });
}

// Poll (.get(), not a realtime listen) until the backend writes the mirror doc
// /access/FIREBASE/users/{uid}/customers/{uuid}. The rule guarding /customers/{uuid}
// needs it, so selecting the account before it lands fails with a permissions error.
// Rejects on timeout so the caller can clean up. The mirror usually lands in 2-3s but has
// been seen taking over two minutes, so allow 40s before giving up.
function waitForCustomerMirror(dispatch, uid, customerUuid, tries = 80, intervalMs = 500) {
  return new Promise((resolve, reject) => {
    let attempt = 0;

    const check = () => {
      attempt += 1;

      dispatch('GetCustomersUuidsByUserUid', uid)
        .then((uuids) => {
          if (uuids?.includes(customerUuid)) {
            resolve(uuids);
          } else if (attempt >= tries) {
            reject(Error('customer-mirror-timeout'));
          } else {
            setTimeout(check, intervalMs);
          }
        })
        .catch(() => {
          if (attempt >= tries) {
            reject(Error('customer-mirror-timeout'));
          } else {
            setTimeout(check, intervalMs);
          }
        });
    };

    check();
  });
}

// The customer doc /customers/{uuid} is a separate backend write that can land after
// the mirror. Binding before it exists yields an empty snapshot (is_account = false),
// so poll until it exists. Rejects on timeout.
function waitForCustomerDoc(getters, customerUuid, tries = 30, intervalMs = 500) {
  const ref = db.collection(getters.collection_customers_name).doc(customerUuid);

  return new Promise((resolve, reject) => {
    let attempt = 0;

    const check = () => {
      attempt += 1;

      ref.get()
        .then((doc) => {
          if (doc.exists) {
            resolve(customerUuid);
          } else if (attempt >= tries) {
            reject(Error('customer-doc-timeout'));
          } else {
            setTimeout(check, intervalMs);
          }
        })
        .catch(() => {
          if (attempt >= tries) {
            reject(Error('customer-doc-timeout'));
          } else {
            setTimeout(check, intervalMs);
          }
        });
    };

    check();
  });
}

const state = {
  auth_data: null,
  is_processing_auth_check: false,
};

const getters = {
  jwt: (state) => (state.auth_data && state.auth_data.JWT) || '',
  jwt_expiry: (state) => (state.auth_data && state.auth_data.JWTExpiry && state.auth_data.JWTExpiry) || 0,

  jwt_is_valid: (state, getters) => (now = Date.now()) => getters.jwt_expiry > Math.round((now + (5 * 60_000)) / 1_000), // five minutes reserve
  jwt_is_expired: (state, getters) => !getters.jwt_is_valid(),
  is_processing_auth_check: (state) => state.is_processing_auth_check,

  // Current identity information
  id_uuid: (state) => (state.auth_data && state.auth_data.UUID) || '',
  id_spid: (state) => (state.auth_data && state.auth_data.SPID) || '',
  id_first_name: (state) => (state.auth_data && state.auth_data.FirstName) || '',
  id_last_name: (state) => (state.auth_data && state.auth_data.LastName) || '',
  id_email: (state) => (state.auth_data && (state.auth_data.Email || state.auth_data.AuthID)) || '',
  id_phone: (state) => (state.auth_data && state.auth_data.MobileNumber) || '',
  id_provider: (state) => (state.auth_data && state.auth_data.IdentityProvider) || '',
  id_auth_id: (state) => (state.auth_data && state.auth_data.AuthID) || '',

  id_display_name: (state, getters) => [getters.id_first_name, getters.id_last_name].filter((x) => x).join(' ') || getters.id_email || '',
};

const mutations = {
  mut_is_processing_auth_check (state, is_processing_auth_check) {
    state.is_processing_auth_check = is_processing_auth_check;
  },

  // mut_auth_data stores the raw Authentication response
  mut_auth_data(state, newAuthData) {
    if (newAuthData && newAuthData.JWT && newAuthData.JWTExpiry && newAuthData.JWTExpiry > Math.round(new Date().getTime() / 1000)) {
      state.auth_data = newAuthData;
      state.jwt = newAuthData.JWT;
      state.jwt_expiry = newAuthData.JWTExpiry;
    //   dynStorage.set('jwt', state.jwt);
    //   dynStorage.set('jwt_expiry', state.jwt_expiry);
    } else {
      state.auth_data = null;
      state.jwt = null;
      state.jwt_expiry = 0;
    //   dynStorage.remove('jwt');
    //   dynStorage.remove('jwt_expiry');
    }
  },

  // mut_update_jwt is desgined to be called from the Axios handler. When the
  // axios middleware recieves the X-Auth-Token + X-Auth-Token-Expiry headers
  // in the HTTP(S) response, then the backend has auto-renewed the token,
  // and it should call this to update the locally cached JWT and JWTExpiry
  mut_update_jwt(state, { JWT, JWTExpiry }) {
    if (JWT && JWTExpiry && JWTExpiry > Math.round(new Date().getTime() / 1000)) {
      state.jwt = JWT;
      state.jwt_expiry = JWTExpiry;
    //   dynStorage.set('jwt', state.jwt);
    //   dynStorage.set('jwt_expiry', state.jwt_expiry);
    } else {
      state.auth_data = null;
      state.jwt = null;
      state.jwt_expiry = 0;
    //   dynStorage.remove('jwt');
    //   dynStorage.remove('jwt_expiry');
    }
  },

  // mut_logout is designed to set the auth state back to the original
  // (logged out) state
  mut_logout(state) {
    // dynStorage.remove('jwt');
    // dynStorage.remove('jwt_expiry');

    state.auth_data = null;
    state.jwt = null;
    state.jwt_expiry = 0;
  },
};

const actions = {
  // LoginEmailPassword is a wrapped for AuthCheck
  // It is intended to be used for LOCAL Email and Password authentication
  LoginEmailPassword({ dispatch }, { login, pass }) {
    return dispatch('AuthCheck', {
      IdentityProvider: 'LOCAL',
      AuthID: login,
      AuthCode: pass,
    });
  },

  RegisterUser({ dispatch }, { email, passnew, firstname, lastname, phone }) {
    return dispatch('Auth', {
      IdentityProvider: 'LOCAL',
      AuthID: email,
      AuthCode: passnew,
      Email: email,
      MobileNumber: phone,
      FirstName: firstname,
      LastName: lastname,
    });
  },

  registerCustomer({ getters, dispatch }, sign) {
    // SSO fallback.
    const sso = getters.sso_signup_data

    return dispatch('Auth', {
      IdentityProvider: 'FIREBASE',
      AuthID: getters.auth_user_uid,
      AuthCode: getters.auth_token,
      Email: sign?.email || getters.auth_user_email || sso?.email,
      MobileNumber: sign?.phone || getters.account_phone || sso?.mobile_number,
      FirstName: sign?.firstname || getters.auth_user_first_name || sso?.first_name,
      LastName: sign?.lastname || getters.auth_user_last_name || sso?.last_name,
      NotifySpecialOffers: sign?.offers ? 2 : getters.mockup_user?.NotifySpecialOffers ? 2 : 0,
      NotifyNewsletters: sign?.newsletters ? 2 : getters.mockup_user?.NotifyNewsletters ? 2 : 0,
    })
  },

  // Create the MVNE customer + bind the account. Shared by the SSO no-account modal on
  // sign-in and eShop. Callers own the UI and any redirect/continuation.
  RegisterCustomerAndBindAccount({ getters, dispatch, commit }, sign) {
    const store = this;

    // Resolve identity from the live Firebase user (store auth_user/token can be empty
    // here — set async by FirebaseOnAuth). Fallback to captured SSO data; fail loudly if
    // there's no uid rather than posting an empty AuthID and orphaning the marker doc.
    const fb_user = firebase.auth().currentUser;
    const sso = getters.sso_signup_data;
    const resolved_uid = fb_user?.uid || sso?.uid || getters.auth_user_uid;

    const ensure_identity = fb_user
      ? fb_user.getIdToken()
          .then((token) => {
            commit('SAVE_AUTH_USER', fb_user);
            commit('SAVE_AUTH_TOKEN', token);
          })
          .catch(() => {})
      : Promise.resolve().then(() => {
          const sso_token = getters.sso_signup_data?.firebase_id_token;
          if (sso_token) commit('SAVE_AUTH_TOKEN', sso_token);
        });

    return ensure_identity
      .then(() => {

        // No uid: fail loudly instead of posting an empty AuthID and orphaning the marker doc.
        if (!resolved_uid) {
          Sentry.captureException(Error('sso-register-missing-identity'), {
            tags: { flow: 'sso-signup-create', reason: 'missing-identity' },
            extra: { hasFbUser: Boolean(fb_user), hasSso: Boolean(getters.sso_signup_data) },
          });

          const handled = Error(i18n.t('sso_create_account_failed'));
          handled.handled = true;
          return Promise.reject(handled);
        }

        return dispatch('registerCustomer', sign);
      })
      .then((res) => {
        const role = res?.Roles?.[0];

        if (role) {
          const { CustomerSPID, CustomerUUID } = role;

          const NotifyNewsletters = (sign?.newsLetters || sign?.newsletters) ? 2 : getters.mockup_user?.NotifyNewsletters ? 2 : 0;
          const NotifySpecialOffers = sign?.offers ? 2 : getters.mockup_user?.NotifySpecialOffers ? 2 : 0;
          const ExternalRef5 = sign?.external_id || getters.mockup_user?.ExternalID || sso?.membership_id;

          // SSO loyalty flag, kept as a string.
          const ExternalRef3 = sso?.loyalty_member || getters.mockup_user?.LoyaltyMember;

          // SSO customer GUID.
          const ExternalRef4 = sso?.external_customer_id || getters.mockup_user?.ExternalCustomerID;

          const payload = {
            NotifySpecialOffers: NotifySpecialOffers === 2 ? 'EMAIL' : 'NO_NOTIFICATION',
            NotifyNewsletters: NotifyNewsletters === 2 ? 'EMAIL' : 'NO_NOTIFICATION',
          };

          if (ExternalRef5) {
            payload.ExternalRef5 = ExternalRef5;
          }

          if (ExternalRef3) {
            payload.ExternalRef3 = String(ExternalRef3);
          }

          if (ExternalRef4) {
            payload.ExternalRef4 = String(ExternalRef4);
          }

          // Ask the reseller to write the mirror doc for the new role. The onboarding
          // adopt_by_* flows all do this; this path never did, so the mirror could be
          // missing forever. Harmless if the backend already wrote it.
          const request_mirror = dispatch('UpdateFirebaseAccess', { data: { AuthResponse: res } })
            .catch((authRolesError) => {
              Sentry.captureException(authRolesError, {
                tags: { flow: 'sso-signup-create', reason: 'auth-roles-failed' },
                extra: { uid: resolved_uid, CustomerUUID },
              });
            });

          // Reading /customers/{uuid} is gated on the backend-written mirror doc, so wait
          // for the mirror, then for the doc itself, before binding the account.
          return request_mirror
            .then(() => waitForCustomerMirror(dispatch, resolved_uid, CustomerUUID))
            .then((uuids) => waitForCustomerDoc(getters, CustomerUUID).then(() => uuids))
            .then((uuids) => {
              commit('SET_ACCOUNT_CUSTOMERS_UUIDS', uuids?.length ? uuids : [CustomerUUID]);

              // Patch after provisioning; the reseller rejects it while the customer is new.
              return dispatch('api_customer/SetCustomerFields', { payload, SPID: CustomerSPID, UUID: CustomerUUID })
                .catch((setCustomerFieldsError) => { console.warn({ setCustomerFieldsError }); })
                .then(() => res);
            })
            .catch((mirrorError) => {
              // Backend never finished provisioning. Clean up the FE marker doc, log it,
              // and surface a retryable message (keep the Firebase user + customer for retry).
              Sentry.captureException(mirrorError, {
                tags: { flow: 'sso-signup-create', reason: mirrorError?.message || 'customer-provision-timeout' },
                extra: { uid: resolved_uid, CustomerUUID },
              });

              const handled = Error(i18n.t('sso_create_account_failed'));
              handled.handled = true;

              return dispatch('DeleteAccount', resolved_uid)
                .catch(() => {})
                .then(() => Promise.reject(handled));
            });
        }

        // No roles in the response (unexpected): fall back to the realtime bind.
        return dispatch('BindAccountCustomers')
          .then(() => waitForAccountCustomers(store, getters))
          .then((uuids) => {
            commit('SET_ACCOUNT_CUSTOMERS_UUIDS', uuids?.length ? uuids : getters.account_customers_uuids);

            return dispatch('UnbindAccountCustomers');
          })
          .then(() => res);
      })
      .catch((error) => {
        // Already handled (cleaned up + friendly message) — pass it straight through.
        if (error?.handled) {
          return Promise.reject(error);
        }

        Sentry.captureException(error, {
          tags: { flow: 'sso-signup-create', reason: 'register-failed' },
        });

        // Register/token failed: fully roll back so no orphan Firebase marker is left
        // (SSO has no `sign`, so also key off sso_signup_data), then re-reject.
        if (sign?.signed_up || getters.sso_signup_data) {
          return dispatch('DeleteAccount', resolved_uid)
            .then(() => dispatch('FirebaseUserDelete'))
            .catch(() => {})
            .then(() => Promise.reject(error));
        }

        return Promise.reject(error);
      });
  },

  ChangePass({ dispatch }, { pass, passnew }) {
    return dispatch('UpdateLocalUser', {
      Password: passnew,
    });
  },

  // Auth makes the API call to add a new local user
  Auth({ dispatch, getters, commit }, payload) {
    payload.SPID = getters.app_spid;
    payload.IncludeRoles = true;

    return axios
      .post(`/v2/:spid/auth`, payload, { refresh: false })
      .then((response) => {
        if (!response.data) {
          return Promise.reject(Error(i18n.t('no_payload')));
        }

        commit('mut_auth_data', response.data);

        return Promise.resolve(response.data);
      })
      .catch((error) => {
        if (error) {
          console.log('Auth ERROR:', error);
          return Promise.reject(error);
        }
        return Promise.reject(Error(`${i18n.t('unknown_error_in')} Auth`));
      });
  },

  AuthCheckViaFirebaseToken({ dispatch, commit }) {
    const user = firebase.auth().currentUser;

    if (!user) {
      commit('mut_is_processing_auth_check', false);

      return Promise.reject(Error(i18n.t('no_user')));
    };

    const firebaseId = user.uid;

    return user.getIdToken().then(firebaseToken => {
      return dispatch('AuthCheck', {
        IdentityProvider: 'FIREBASE',
        AuthID: firebaseId,
        AuthCode: firebaseToken,
      });
    });
  },

  // AuthCheck makes the API call to check authentication
  AuthCheck({ dispatch, getters, commit }, payload) {
    commit('mut_is_processing_auth_check', true);

    payload.SPID = getters.app_spid;
    payload.IncludeRoles = true;

    return axios
      .post(`/v2/:spid/auth_check`, payload, { refresh: false, is_auth_check: true  })
      .then((response) => {
        if (!response.data) {
          commit('mut_is_processing_auth_check', false);

          return Promise.reject(Error(i18n.t('no_payload')));
        }

        commit('mut_auth_data', response.data);
        commit('mut_is_processing_auth_check', false);

        return Promise.resolve();
      })
      .catch((error) => {
        commit('mut_is_processing_auth_check', false);

        if (error) {
          console.log('AuthCheck ERROR:', error);
          return Promise.reject(error);
        }
        return Promise.reject(Error(`${i18n.t('unknown_error_in')} AuthCheck`));
      });
  },

  // AutoLogin is designed to be run when the application loads
  // to resume/recover the application authentication state
  // It will re-authenticate with the API to ensure the JWT is still valid
  AutoLogin({ dispatch, commit }) {
    const storedJWT = dynStorage.get('jwt');
    if (!storedJWT) {
      commit('mut_logout');
      // AutoLogin must always return a resolved Promise
      return Promise.resolve();
    }

    return dispatch('AuthCheck', {
      IdentityProvider: 'JWT_REFRESH',
      AuthID: 'jwt',
      AuthCode: storedJWT,
    })
      .then(() => {
        // AutoLogin must always return a resolved Promise
        return Promise.resolve();
      })
      .catch(() => {
        // AutoLogin must always return a resolved Promise
        return Promise.resolve();
      });
  },

  // Logout logs the user out, and removes all authentication state
  Logout({ dispatch, commit }) {
    commit('mut_logout');

    commit('resetPaymentMethods')
    commit('resetPrepayProduct')
    commit('resetPostpayProduct')
    commit('resetUFBProduct')
  },

  // setJWT is called from axios middleware to update the local cache
  // of the JWT and JWT Expirty
  setJWT({ getters, commit }, { JWT, JWTExpiry }) {
    if (!JWT || JWT == '-' || JWTExpiry == -1) {
      commit('mut_logout');
      return;
    }
    if (!JWTExpiry) {
      commit('mut_update_jwt', { JWT, JWTExpiry: getters.jwt_expiry });
      return;
    }
    commit('mut_update_jwt', { JWT, JWTExpiry });
  },

  // UpdateLocalUser makes the API call to update user's data
  UpdateLocalUser({ getters, commit }, payload) {
    payload.SPID = getters.app_spid;

    return axios
      .put(`/v2/:spid/auth/LOCAL/${getters.id_auth_id}`, payload, { refresh: false })
      .then((response) => {
        if (!response.data) {
          return Promise.reject(Error(i18n.t('no_payload')));
        }

        commit('mut_auth_data', response.data);

        return Promise.resolve();
      })
      .catch((error) => {
        if (error) {
          console.log('UpdateLocalUser ERROR:', error);
          return Promise.reject(error);
        }
        return Promise.reject(Error(`${i18n.t('unknown_error_in')} UpdateLocalUser`));
      });
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
